import * as React from "react"

import { cn } from "@/lib/utils"
import { TableContext, flattenChildren, contentText } from './table-context'
import { ChevronDown } from 'lucide-react'

const Table = React.forwardRef(({ className, children, mobileLayout = 'cards', mobileSummary, ...props }, ref) => {
  const id = React.useId().replace(/[^a-z0-9-]/gi, '');
  const header = flattenChildren(children).find(child => child.type === TableHeader);
  const headerRow = header && flattenChildren(header.props.children).find(child => child.type === TableRow);
  const labels = headerRow ? flattenChildren(headerRow.props.children).map(cell => contentText(cell.props?.children)) : [];
  return (
    <TableContext.Provider value={{ labels, mobileSummary }}>
      <div className="responsive-table relative min-w-0 w-full max-w-full overflow-auto" data-mobile-layout={mobileLayout}>
        <table ref={ref} className={cn('w-full caption-bottom text-sm', className)}
          data-testid={`table-${id}`} {...props}>{children}</table>
      </div>
    </TableContext.Provider>
  );
})
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, children, ...props }, ref) => {
  const { labels, mobileSummary } = React.useContext(TableContext);
  const id = React.useId().replace(/[^a-z0-9-]/gi, '');
  const [expanded, setExpanded] = React.useState(false);
  const cells = flattenChildren(children);
  const isSecondary = (cell, index) => cell.type === TableCell && !cell.props.colSpan && mobileSummary && labels[index] && !/actions?/i.test(labels[index]) && !mobileSummary.includes(labels[index]);
  const hasDetails = cells.some(isSecondary);
  return <tr ref={ref} className={cn('border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted', className)}
    data-expanded={expanded} data-has-details={!!hasDetails}
    data-testid={`table-row-${id}`} tabIndex={props.onClick ? 0 : undefined}
    onKeyDown={props.onClick ? e => { if (e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) { e.preventDefault(); props.onClick(e); } } : undefined}
    {...props}>
    {cells.map((cell, index) => cell.type === TableCell
      ? React.cloneElement(cell, {
        'data-testid': cell.props['data-testid'] || `${props['data-testid'] || `table-row-${id}`}-cell-${index}`,
        'data-label': cell.props.colSpan > 1 ? '' : labels[index] || '',
        'data-secondary': !!isSecondary(cell, index),
        'data-cell-kind': cell.props.colSpan > 1 ? 'full' : /^(name|employee)$/i.test(labels[index]) ? 'identity' : /^actions?$/i.test(labels[index]) ? 'actions' : /^(reason|details|notes)$/i.test(labels[index]) ? 'full' : 'value'
      })
      : cell)}
    {hasDetails && <td className="mobile-record-toggle" data-cell-kind="toggle">
      <button type="button" data-testid={`${props['data-testid'] || `table-row-${id}`}-details-toggle`}
        aria-expanded={expanded} onClick={event => { event.stopPropagation(); setExpanded(value => !value); }}
        className="flex items-center justify-center gap-1 text-sm font-medium text-red-700 rounded border border-neutral-200 px-3 py-2 hover:bg-red-50 transition-colors">
        {expanded ? 'Less' : 'Details'}<ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
    </td>}
  </tr>;
})
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
